import React, { createContext, useContext, useEffect, useState } from 'react';
import ManagerSubscriptionsApiClient from '../api-client/ManagerSubscriptionsApiClient';
import { store } from '../store/store';
import { useLocation, useNavigate } from 'react-router-dom';

export const ManagerSubscriptionContext = createContext(null);

export const useManagerSubscription = () => useContext(ManagerSubscriptionContext);

export const ManagerSubscriptionProvider = ({ children }) => {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();
    const userType = sessionStorage.getItem('userType');

    const fetchStatus = async () => {
        if (userType !== 'struttura') {
            setLoading(false);
            return;
        }
        
        try {
            const state = store.getState();
            const token = state.auth.auth.idToken;
            const apiClient = new ManagerSubscriptionsApiClient();
            const res = await apiClient.getStatus(token);
            if (!res.error) {
                setSubscription(res);
            } else {
                console.error("Errore fetch subscription status:", res.error);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
    }, [userType]);

    useEffect(() => {
        if (loading || userType !== 'struttura') return;
        
        if (subscription) {
            // I trial gratuiti (priceEur === 0) non richiedono dati di fatturazione
            const isTrial = subscription.priceEur === 0;

            // 1. Abbonamento attivo (prova gratuita o pagato): l'utente continua a usarlo.
            //    Un eventuale checkout in corso o bonifico in attesa NON blocca l'accesso.
            if (subscription.active) {
                // Solo per un abbonamento PAGATO non-trial mancano i dati di fatturazione → raccoglierli.
                if (subscription.status !== 'free' && !isTrial && subscription.billingDataInserted === false) {
                    if (location.pathname !== '/abbonamento/success') {
                        navigate('/abbonamento/success', { replace: true });
                    }
                }
                return;
            }

            // 2. Nessun abbonamento attivo. Bonifico una tantum in attesa di accredito:
            //    tieni l'utente sulla pagina informativa invece di mandarlo su /inactive.
            if (subscription.paymentPending) {
                if (location.pathname !== '/abbonamento/success') {
                    navigate('/abbonamento/success', { replace: true });
                }
                return;
            }

            // Se l'utente sta già navigando in una pagina di abbonamento, non fare redirect continui
            if (location.pathname.startsWith('/abbonamento')) return;

            if (subscription.status === 'none') {
                navigate('/abbonamento', { replace: true });
            } else if (subscription.status === 'expired') {
                // Fine del periodo di prova gratuito: non è un vero rinnovo fallito,
                // quindi salta la pagina "Rinnovo automatico non riuscito" e porta
                // direttamente alla sottoscrizione di un abbonamento.
                if (isTrial) {
                    navigate('/abbonamento', { replace: true });
                } else {
                    navigate('/abbonamento/expired', { replace: true });
                }
            } else if (subscription.status === 'refunded') {
                // Account rimborsato/disattivato: pagina di supporto (vicolo cieco voluto).
                navigate('/abbonamento/inactive', { replace: true });
            } else if (subscription.status === 'inactive') {
                // Checkout avviato e non completato: lascia riprovare il pagamento.
                navigate('/abbonamento', { replace: true });
            } else {
                // Trial scaduta (status ancora 'active' ma endDate < oggi)
                // o qualsiasi altro caso non gestito: porta alla pagina di sottoscrizione.
                navigate('/abbonamento', { replace: true });
            }
        }
    }, [subscription, loading, location.pathname, navigate, userType]);

    return (
        <ManagerSubscriptionContext.Provider value={{ subscription, loading, fetchStatus }}>
            {children}
        </ManagerSubscriptionContext.Provider>
    );
};
